<template>
  <div class="timeline__child d-md-flex align-stretch">
    <div class="timeline__child__zone flex-grow-1 left d-flex justify-end">
      <div v-if="product.image" class="timeline__child__image pl-1-5 pr-1-5">
        <lazy-image
          :image="{
            url: `${product.image}?aspect_ratio=4:3`,
            alt: '',
            dimensions: { width: 360, height: 270 },
          }"
          provider="bunny"
        />
      </div>
    </div>
    <!-- <div class="line"></div> -->
    <div class="circle"></div>
    <div
      class="timeline__child__zone pl-md-1-5 pr-md-1-5 pt-3 pt-md-0 pb-2 pb-md-3 flex-grow-1 right"
    >
      <p class="h5 mb-0-4" :class="{ 'text-tag': product.en_avant }">
        {{ product.label }}
      </p>
      <p v-if="product.catchPhrase" class="text-color-secondary-1 mb-0-4">
        {{ product.catchPhrase }}
      </p>
      <div class="timeline__date-and-link d-flex align-center">
        <p class="mb-1-5 mb-md-0">
          <strong>
            {{ $t('the') }}
            {{ $d(product.date, 'short') }}
          </strong>
        </p>

        <span class="d-none d-md-block pl-1 pr-1">-</span>

        <transition name="appear-right" mode="out-in">
          <a
            v-if="showEmail"
            key="cancel"
            class="d-flex align-center link--underline"
            @click.prevent="onCancel"
          >
            <icon name="cross" />
            <span class="lh-1 ml-0-4">
              <strong class="text-color-secondary text-small">{{
                $t('action.cancel')
              }}</strong></span
            >
          </a>
          <a
            v-else
            key="alert"
            :href="`#${product.uid}`"
            class="d-flex align-center link--underline"
            @click.prevent="onAlertClick(product.uid)"
          >
            <icon name="bell" :full="isSubscribed" />
            <span class="lh-1 ml-0-4">
              <strong class="text-color-secondary text-small">{{
                $t(
                  isSubscribed ? 'alert.tooltip.remove' : 'alert.tooltip.create'
                )
              }}</strong></span
            >
          </a>
        </transition>
      </div>

      <transition name="appear-top">
        <form
          v-if="showEmail"
          class="mt-1-5"
          novalidate
          @submit.prevent="onAlertEmailSubmit"
        >
          <div class="d-md-flex">
            <form-input
              :id="`timeline-create-alert-${product.uid}-email`"
              ref="inputEmail"
              v-model="email"
              type="email"
              :label="$t('input.email.label')"
              label-visible
              label-placeholder
              first-submit
              required
            />

            <primary-button
              type="submit"
              :text="$t('action.subscribe')"
              class="mt-1 mt-md-0 ml-md-1"
              :loading="loadingEmail"
            />
          </div>
        </form>
      </transition>

      <transition name="appear-top">
        <p
          v-if="subscribeError"
          class="text-color-error text-small text-right mb-0"
        >
          {{ $t('product.alert.error') }}
        </p>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import services from '@/services'

import Icon from '@/components/icons/Icon'
import FormInput from '@/components/form/Input'
import PrimaryButton from '@/components/buttons/Primary'
import LazyImage from '@/components/Image/LazyImage.vue'
import { ORIGIN } from '@/const'
import { EVENTS } from '@/const/events'

export default {
  name: 'TimelineSectionChild',
  components: {
    Icon,
    FormInput,
    PrimaryButton,
    LazyImage,
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    isSubscribed: false,
    subscribeError: false,
    showEmail: false,
    email: '',
    loadingEmail: false,
  }),
  computed: {
    ...mapGetters({
      connectedEmail: 'subscriptions/connectedEmail',
      account: 'subscriptions/account',
      isSubscriptionsLoading: 'subscriptions/isLoading',
    }),
  },
  watch: {
    account() {
      if (this.account.email && !this.showEmail) {
        this.email = this.account.email
      }

      if (this.account.email && !this.connectedEmail) {
        this.isSubscribed = this.account.isSubscribed(this.product.uid)
      }
    },
  },
  mounted() {
    if (
      this.connectedEmail === null ||
      (this.connectedEmail !== '' && this.account.email === null)
    ) {
      this.$nuxt.$on(EVENTS.USER_STATE_LOADED, (_) => {
        this.isSubscribed = this.account.isSubscribed(this.product.uid)
      })
    } else {
      this.isSubscribed = this.account.isSubscribed(this.product.uid)
    }

    this.email = this.account.email || ''
  },
  methods: {
    ...mapActions({
      subscribe: 'subscriptions/subscribe',
      unsubscribe: 'subscriptions/unsubscribe',
      setGuestEmail: 'subscriptions/setGuestEmail',
      getUserSubscriptions: 'subscriptions/getUserSubscriptions',
    }),
    validate() {
      if (!this.$refs.inputEmail.isValid) {
        return false
      }

      return true
    },
    async onAlertClick() {
      const product = {
        productSlug: this.product.uid,
        productName: this.product.label,
        productId: '',
        productImage: 'https://',
        location: ORIGIN.AGENDA_TIMELINE,
      }

      if (this.isSubscribed) {
        return await this.unsubscribe(product)
          .then(() => {
            this.isSubscribed = this.account.isSubscribed(this.product.uid)

            this.$track.bisUnSubscribe(this.product.uid, ORIGIN.AGENDA_TIMELINE)

            this.showToaster(`<div>
                ${this.$t('product.alert.deactivated', {
                  produit: this.product.label,
                })}
              </div>
              `)
          })
          .catch((err) => {
            this.$logError.captureException(err)
            this.subscribeError = true
          })
      }

      if (this.connectedEmail === '' || this.account.email === null) {
        this.showEmail = true

        await this.$nextTick()

        document
          .getElementById(`timeline-create-alert-${this.product.uid}-email`)
          ?.focus()

        window.dispatchEvent(new Event('resize'))

        return
      }

      return await this.subscribe(product)
        .then(() => {
          this.isSubscribed = this.account.isSubscribed(this.product.uid)

          this.$nuxt.$emit(EVENTS.ASK_NOTIFICATION, ORIGIN.AGENDA_TIMELINE)
          this.$track.bisSubscribe(
            this.account.email,
            this.product.uid,
            ORIGIN.AGENDA_TIMELINE
          )

          this.showToaster(`<div>
            ${this.$t('product.alert.activated', {
              produit: this.product.label,
              email: this.account.email,
            })}
          </div>
          `)
        })
        .catch((err) => {
          this.$logError.captureException(err)
          this.subscribeError = true
        })
    },
    async onAlertEmailSubmit() {
      if (this.validate()) {
        this.loadingEmail = true

        try {
          if (
            (this.connectedEmail === '' ||
              this.connectedEmail !== this.email) &&
            this.account.email !== this.email
          ) {
            await this.setGuestEmail(this.email)
          }

          await this.subscribe({
            productSlug: this.product.uid,
            productName: this.product.label,
            productId: '',
            productImage: 'https://',
            location: ORIGIN.AGENDA_TIMELINE,
          }).then(() => {
            this.isSubscribed = this.account.isSubscribed(this.product.uid)

            this.getUserSubscriptions(this.email)

            this.$nuxt.$emit(EVENTS.ASK_NOTIFICATION, ORIGIN.AGENDA_TIMELINE)
            this.$track.bisSubscribe(
              this.account.email,
              this.product.uid,
              ORIGIN.AGENDA_TIMELINE
            )
          })

          await this.showModal()

          this.showEmail = false
          this.loadingEmail = false
        } catch (err) {
          this.$logError.captureException(err)
          this.subscribeError = true
          this.loadingEmail = false
        }
      }
    },
    async onCancel() {
      this.showEmail = false

      await this.$nextTick()

      setTimeout((_) => {
        window.dispatchEvent(new Event('resize'))
      }, 400)
    },
    showToaster(text) {
      this.$toasted.clear()
      this.$toasted.show(text, {
        position: 'bottom-center',
        duration: 5000,
        keepOnHover: true,
        action: {
          text: '',
          icon: 'close',
          class: 'toasted__close',
          onClick: (_, toastObject) => {
            toastObject.goAway(0)
          },
        },
      })
    },
    showModal() {
      services.accountManagerService
        .getCustomerStatus(this.email)
        .then((res) => {
          this.$nuxt.$emit(EVENTS.SHOW_FORCE_CONNECT, {
            email: this.email,
            status: res.data.data.status,
          })
        })
    },
  },
}
</script>

<style lang="scss">
.timeline__child {
  position: relative;

  form {
    padding: var(--spacing);
    background-color: var(--secondary-color-5);

    @include mq($until: tablet) {
      width: 100%;
    }

    .input {
      margin-bottom: 0;
    }

    input,
    .input_container,
    .input__valid,
    .input__error-a11y {
      background-color: var(--secondary-color-5);
    }

    .action {
      @include mq($until: tablet) {
        margin-left: auto;
        margin-right: auto;
      }
    }
  }

  .circle {
    position: absolute;
    top: calc(var(--spacing) * 0.6);
    left: 50%;
    transform: translateX(-50%);
    z-index: 3;
  }

  .text-tag {
    background-color: var(--yellow-color);
  }

  .input input {
    @include mq($from: tablet) {
      min-width: rem(300px);
    }
  }

  .input__error {
    margin-bottom: 0;
    margin-left: 0;
  }

  .right {
    display: flex;
    flex-direction: column;
    align-items: center;

    @include mq($from: tablet) {
      align-items: flex-start;
    }
  }

  @include mq($from: tablet) {
    &:nth-child(odd) {
      .left {
        order: 2;
        justify-content: start;
      }

      .right {
        order: 1;
        align-items: flex-end;
      }

      .timeline__date-and-link {
        flex-direction: row-reverse;
      }
    }
  }

  @include mq($until: tablet) {
    .timeline__date-and-link {
      flex-direction: column;
    }
  }

  &__zone {
    position: relative;
    flex-shrink: 0;
    flex-basis: calc(50% - #{rem(1px)});
    z-index: 1;

    @include mq($until: tablet) {
      background-color: var(--tertiary-color);
      text-align: center;
    }
  }

  &__image {
    width: rem(360px);
  }
}
</style>
